import { addDays } from 'date-fns';
import { convertLocalDateToUTCDate } from '@izimi/utils';
import CommunicationsApi from '../communications/CommunicationsApi';
import DocumentsApi from '../documents/DocumentsApi';
import { ApiDocumentSource } from '../documents/types';
import { makeCombinedPaginatedQuery } from '../util/api';
import { sharedItemsCollection } from './types';
const EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS = 31;
class SharedApi {
    fetchService;
    documentsApi;
    communicationsApi;
    constructor(fetchService) {
        this.fetchService = fetchService;
        this.documentsApi = new DocumentsApi(this.fetchService);
        this.communicationsApi = new CommunicationsApi(this.fetchService);
    }
    getExpiringCommunications = async ({ searchTerm, ...params }) => {
        const beforeScheduledDeleteDate = convertLocalDateToUTCDate(addDays(new Date(), EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS)).toISOString();
        // GetCommunications doesn't supports filter parameters yet
        if ([params.labels, params.ownerId].some(param => param != null)) {
            return {
                collection: sharedItemsCollection.EXPIRING_COMMUNICATION_DETAILS,
                items: [],
                total: 0,
                metadata: { numberUnseenExpiringCommunications: 0 },
            };
        }
        const res = await this.communicationsApi.getCommunications({ ...params, beforeScheduledDeleteDate, searchText: searchTerm });
        return {
            collection: sharedItemsCollection.EXPIRING_COMMUNICATION_DETAILS,
            items: res.communications ?? [],
            total: res.total ?? 0,
            metadata: { numberUnseenExpiringCommunications: res.numberUnseenCommunications },
        };
    };
    getNotExpiringCommunications = async ({ searchTerm, ...params }) => {
        const boundaryDate = addDays(new Date(), EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_DAYS);
        const afterScheduledDeleteDate = convertLocalDateToUTCDate(addDays(boundaryDate, 1)).toISOString();
        // GetCommunications doesn't supports filter parameters yet
        if ([params.labels, params.ownerId].some(param => param != null)) {
            return {
                collection: sharedItemsCollection.NOT_EXPIRING_COMMUNICATION_DETAILS,
                items: [],
                total: 0,
                metadata: { numberUnseenCommunications: 0 },
            };
        }
        const res = await this.communicationsApi.getCommunications({ ...params, afterScheduledDeleteDate, searchText: searchTerm });
        return {
            collection: sharedItemsCollection.NOT_EXPIRING_COMMUNICATION_DETAILS,
            items: res.communications ?? [],
            total: res.total ?? 0,
            metadata: { numberUnseenCommunications: res.numberUnseenCommunications },
        };
    };
    getPushedDocuments = async ({ searchTerm, ...params }) => {
        const res = await this.documentsApi.getDocuments({
            sources: ApiDocumentSource.Study,
            states: 'PENDING',
            returnUniqueFilterValues: true,
            sort: 'creationDate,desc',
            documentName: searchTerm,
            ...params,
        });
        return {
            collection: sharedItemsCollection.PUSHED_DOCUMENTS,
            items: res.documents,
            total: res.total,
            metadata: {
                ...res.uniqueFilterValues,
                numberPendingPushedDocuments: res.total,
            },
        };
    };
    getSharedDocuments = async ({ searchTerm, ...params }) => {
        const res = await this.documentsApi.getDocuments({
            sources: ApiDocumentSource.Shared,
            states: ['ACTIVE', 'PENDING'],
            returnUniqueFilterValues: true,
            sort: 'creationDate,desc',
            documentName: searchTerm,
            ...params,
        });
        return {
            collection: sharedItemsCollection.SHARED_DOCUMENTS,
            items: res.documents,
            total: res.total,
            metadata: res.uniqueFilterValues,
        };
    };
    /**
     * Retrieves a combined collection of shared items including expiring communications, pushed documents,
     * non-expiring communications, and shared documents.
     *
     * @returns A combined paginated query function that returns:
     * - Expiring communications (those scheduled for deletion within 31 days)
     * - Pushed documents (study documents in PENDING state)
     * - Non-expiring communications
     * - Shared documents (documents with ACTIVE or PENDING state)
     *
     * The response includes metadata about unseen items and unique filter values where applicable.
     */
    getSharedItems = makeCombinedPaginatedQuery()({
        queryFns: [this.getExpiringCommunications, this.getPushedDocuments, this.getNotExpiringCommunications, this.getSharedDocuments],
    });
}
export default SharedApi;
